import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { useCreateProject } from '../queries';
import { projectPath } from '../paths';
import { PROJECT_VISIBILITIES, type ProjectVisibility } from '../constants';
import type { ProjectInput } from '../types';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Derive the default identifier the backend would generate, for the preview. */
function suggestIdentifier(name: string): string {
  const first = name.trim().split(/\s+/)[0] ?? '';
  return first.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase();
}

export function CreateProjectDialog({ open, onOpenChange }: CreateProjectDialogProps) {
  const navigate = useNavigate();
  const create = useCreateProject();
  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [visibility, setVisibility] = useState<ProjectVisibility>('public');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Focus the name field when the dialog mounts (a11y-friendly alt to autoFocus).
  const focusOnMount = useCallback((el: HTMLInputElement | null) => el?.focus(), []);

  function reset() {
    setName('');
    setIdentifier('');
    setVisibility('public');
    setDescription('');
    setError(null);
  }

  async function handleSubmit() {
    setError(null);
    const payload: ProjectInput = {
      name: name.trim(),
      identifier: identifier.trim() || undefined,
      visibility,
      description: description.trim() || undefined,
    };
    try {
      const project = await create.mutateAsync(payload);
      reset();
      onOpenChange(false);
      navigate(projectPath(project));
    } catch (e) {
      const err = e as ApiError;
      setError(err.fieldErrors ? Object.values(err.fieldErrors).flat().join(' ') : err.message);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create project</DialogTitle>
          <DialogDescription>
            A project holds work items, boards, modules, cycles and a wiki.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              ref={focusOnMount}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sales"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="project-id">Project ID</Label>
              <Input
                id="project-id"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value.toUpperCase())}
                placeholder={suggestIdentifier(name) || 'SALES'}
                maxLength={10}
              />
              <p className="text-muted-foreground text-xs">Prefixes work items, e.g. SALES-123.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Access</Label>
              <Select value={visibility} onValueChange={(v) => setVisibility(v as ProjectVisibility)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_VISIBILITIES.map((v) => (
                    <SelectItem key={v.value} value={v.value}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="project-desc">Description</Label>
            <Textarea
              id="project-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
              rows={3}
            />
          </div>

          {error ? <p className="text-destructive text-sm">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
